import { z } from 'zod';
import { ALTANET_PICKING_ORDER_STATUSES, altanetPickingNoticeV1Schema, } from '../altanet/picking-notice.js';
import { pickingStateStatusSchema } from '../altanet/picking-state.js';
import { isoDateSchema, isoDateTimeSchema, optionalText, QUANTITY_DECIMAL_SCALE, requiredText, } from '../common/coercion.js';
import { deploymentTierSchema } from '../common/deployment-tier.js';
import { DTE_ORDER_SHARE_STATUSES, dteDocumentStatusSchema, dteDocumentTypeSchema, dteOrderShareStatusSchema, } from '../invoicing/dte.js';
import { JANIS_INVOICE_NUMBER_PATTERN } from '../janis/invoice.js';
/**
 * Simulacion del picking del WMS (F9), para llevar una venta de QA por el
 * picking y hasta el registro en JEF sin Altanet y sin inyectar el
 * `ready-for-invoice` a mano.
 *
 * ## Que se simula y que NO
 *
 * Se simula SOLO el WMS: el middleware arma un aviso de picking v1
 * (`altanet/picking-notice.ts`) con las lineas REALES de la factura de reserva
 * del pedido y lo entrega por **el receptor del webhook de Altanet de verdad**
 * (message log, clave de idempotencia, cola de F9). El worker de F9 lo procesa
 * sin saber que es una simulacion, y llama a Janis **de verdad**
 * (`start-picking` y `pick`).
 *
 * Por eso no se enciende en produccion (`SIMULATION_ENABLED` exige un ambiente
 * que no sea produccion y datos de prueba) **y** contra un dominio de Janis que
 * sea produccion o desconocido responde 403.
 *
 * Contrato NUESTRO: estricto en las dos direcciones.
 */
/** Rutas del monitor, relativas al prefijo global (`/api`). Piden la sesion de Google. */
export const PICKING_SIMULATION_API_PATHS = {
    /** Si el ambiente tiene la simulacion encendida: el monitor decide su menu con esto. */
    capabilities: '/simulation/capabilities',
    orders: '/simulation/picking/orders',
    notices: '/simulation/picking/notices',
};
/**
 * `GET /simulation/capabilities`: si este ambiente tiene la simulacion
 * encendida, para que el monitor decida si ofrece **Simulación** en el menu.
 *
 * 🔴 Es una CORTESIA de la UI, no una barrera: la API mantiene las suyas aunque
 * el menu muestre la simulacion (404 con `SIMULATION_ENABLED=false`, 403 sin
 * rol `operator` o contra un Janis de produccion). Responde 200 siempre, tambien
 * apagada, a cualquier persona con sesion: no revela nada que la UI no muestre.
 *
 * El menu ya no depende de la etiqueta del ambiente con que se compilo el
 * monitor: el servidor de desarrollo dice `desarrollo` y aun asi tiene la
 * simulacion. `deploymentTier` llega de la API y falla cerrado igual que alla
 * (sin `DEPLOYMENT_TIER`, `NODE_ENV=production` es `production`).
 */
export const simulationCapabilitiesSchema = z
    .object({
    /** `SIMULATION_ENABLED`. */
    enabled: z.boolean(),
    /** `F9_PICKING_ENABLED`: apagado, el aviso se registra pero no llega a Janis. */
    pickingEnabled: z.boolean(),
    /** `false` si `JANIS_DOMAIN` es produccion o desconocido (la misma regla que la entrega). */
    janisWritesAllowed: z.boolean(),
    /** El ambiente de despliegue efectivo de la API. */
    deploymentTier: deploymentTierSchema,
})
    .strict();
/**
 * Ruta de Postman, relativa al prefijo global (`/api`). Autenticacion de
 * SISTEMA (`x-amoble-system` + `x-amoble-api-key`): una ruta de sistema no
 * acepta sesiones, por eso es otra ruta y no la del monitor.
 */
export const SIMULATION_ALTANET_PICKING_NOTICES_PATH = '/simulation/altanet/picking-notices';
/**
 * Como avanza el picking simulado:
 *
 * - `progress` (en preparacion): acumulados parciales; el pedido queda EN_PICKING.
 * - `complete` (completo): todo lo pedido; el pedido queda PICKEADO.
 * - `shortage` (con faltante): las lineas indicadas quedan cortas; el pedido
 *   queda PICKEADO_CON_FALTANTE.
 */
export const SIMULATED_PICKING_MODES = ['progress', 'complete', 'shortage'];
export const simulatedPickingModeSchema = z.enum(SIMULATED_PICKING_MODES);
/** Etiquetas de los modos, como se muestran en el monitor. */
export const SIMULATED_PICKING_MODE_LABELS = {
    progress: 'en preparación',
    complete: 'completo',
    shortage: 'con faltante',
};
/** El id de evento del aviso: la misma regla que `Id_evento` en la v1. */
const simulatedEventIdSchema = z
    .string()
    .regex(/^[A-Za-z0-9._:-]{1,128}$/, 'El eventId admite letras, digitos y . _ : - (hasta 128 caracteres).');
/** `true` si el numero cabe en `numeric(x,4)`: la escala de las cantidades. */
function fitsQuantityScale(value) {
    const escalado = value * 10 ** QUANTITY_DECIMAL_SCALE;
    return Math.abs(escalado - Math.round(escalado)) < 1e-6;
}
/** Cantidad acumulada pickeada: cero o mas, con hasta 4 decimales. */
const cumulativePickedSchema = z
    .number({ invalid_type_error: 'Lo pickeado debe ser un numero.' })
    .finite('Lo pickeado debe ser un numero finito.')
    .nonnegative('Lo pickeado no puede ser negativo.')
    .refine(fitsQuantityScale, `Lo pickeado no puede tener mas de ${String(QUANTITY_DECIMAL_SCALE)} decimales.`);
/** Una linea editada por quien simula. */
export const simulatedPickingLineRequestSchema = z
    .object({
    /** `LineNum` de la linea en la factura de reserva. */
    baseLine: z.number().int().nonnegative(),
    /** Lo pickeado ACUMULADO de la linea, en unidades de SAP. */
    picked: cumulativePickedSchema,
    /** Motivo del faltante (solo en `shortage`). */
    reason: optionalText(255),
})
    .strict();
/**
 * `POST /simulation/picking/notices` (monitor) y
 * `POST /simulation/altanet/picking-notices` (Postman).
 *
 * `picked` es ACUMULADO, como en el aviso del WMS. Las lineas que no van:
 *
 * - en `progress` conservan el ultimo acumulado APLICADO;
 * - en `shortage` se pickean completas;
 * - en `complete` no se mandan lineas: se pickea todo.
 *
 * Venta agrupada: como el WMS, la simulacion arma UNA OD por bodega y fecha.
 * `janisOrderId` (el hijo) elige la OD que tiene sus lineas, y el aviso trae
 * TODAS las lineas de esa OD: si dos hijos comparten bodega y fecha, tambien
 * las del hermano, y `lines[].baseLine` puede ser cualquiera de ellas.
 */
export const simulatedPickingNoticeRequestSchema = z
    .object({
    /** El pedido de Janis: el hijo, en una venta agrupada. */
    janisOrderId: requiredText(64),
    mode: simulatedPickingModeSchema,
    /**
     * Id del aviso (`Id_evento`). Si ya se entrego, el servicio REENVIA el
     * cuerpo guardado sin cambios, como hace el WMS al reintentar: el receptor
     * contesta `duplicate` o lo vuelve a encolar. Si no viene, se genera uno.
     */
    eventId: simulatedEventIdSchema.optional(),
    lines: z
        .array(simulatedPickingLineRequestSchema)
        .max(500, 'Un aviso simulado no puede editar mas de 500 lineas.')
        .optional(),
})
    .strict()
    .superRefine((request, ctx) => {
    if (request.mode === 'shortage' &&
        (request.lines === undefined || request.lines.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines'],
            message: 'Un picking con faltante tiene que decir que lineas quedan cortas.',
        });
    }
    if (request.mode === 'complete' && request.lines !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines'],
            message: 'Un picking completo pickea todo lo pedido: no lleva lineas.',
        });
    }
    const vistas = new Set();
    request.lines?.forEach((line, indice) => {
        if (vistas.has(line.baseLine)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lines', indice, 'baseLine'],
                message: `La linea base ${String(line.baseLine)} va dos veces.`,
            });
        }
        vistas.add(line.baseLine);
    });
});
/** Respuesta de la entrega: lo que hizo el receptor con el aviso, y el aviso. */
export const simulatedPickingNoticeResponseSchema = z
    .object({
    janisOrderId: requiredText(64),
    mode: simulatedPickingModeSchema,
    /** El `Id_evento` usado: reenviarlo simula un reintento del WMS. */
    eventId: requiredText(128),
    /** La `Secuencia` del aviso. */
    sequence: z.number().int().min(1),
    /**
     * `queued`: el aviso se encolo. `duplicate`: ese aviso ya se habia
     * procesado. `quarantined`: el receptor lo dejo en cuarentena.
     */
    status: z.enum(['queued', 'duplicate', 'quarantined']),
    /** `true` si se reenvio un aviso ya entregado (mismo `eventId`). */
    redelivery: z.boolean(),
    /** El aviso tal como se entrego al receptor. */
    notice: altanetPickingNoticeV1Schema,
})
    .strict();
/** Una linea pickeable de la factura de reserva (sin el flete `DESPACHO`). */
export const pickingSimulationLineSchema = z
    .object({
    /** `LineNum` en la factura de reserva. */
    baseLine: z.number().int().nonnegative(),
    sku: requiredText(64),
    /** Cantidad de la linea en SAP. */
    requested: z.number().positive(),
    /** Ultimo acumulado APLICADO (el del ultimo aviso procesado); 0 sin avisos. */
    picked: z.number().nonnegative(),
    /** Id de la linea en el pedido de Janis, si se conoce. */
    janisLineId: z.string().nullable(),
})
    .strict();
/**
 * Hasta donde llego la PARTE del pedido en el DTE (`dte_order_share`): su
 * `customData` y su registro en JEF. En una venta agrupada es lo del hijo; el
 * `dte.status` es el de la venta.
 *
 * Es la lista canonica `DTE_ORDER_SHARE_STATUSES` (`invoicing/dte.ts`), la misma
 * del CHECK de la base: no una copia. El nombre queda por compatibilidad.
 */
export const PICKING_SIMULATION_SHARE_STATUSES = DTE_ORDER_SHARE_STATUSES;
export const pickingSimulationShareStatusSchema = dteOrderShareStatusSchema;
/**
 * Un pedido que espera (o tuvo) picking, como lo lista el monitor. En una venta
 * agrupada (`docs/pedidos-de-tienda.md` §1.4) es un pedido HIJO: un renglon por
 * hijo, con la factura de reserva y el DTE de la venta, y su propia bodega,
 * fecha, orden de despacho y parte en JEF. El monitor los agrupa por la factura
 * de reserva (una por venta) y muestra `saleNumber` como etiqueta: el numero de
 * venta solo es unico dentro de su canal.
 *
 * Invariantes (las arma la API; si no se cumplen es un error nuestro):
 * - `jefNumber` es el DTE (`<tipo>-<folio>`), con la parte `-<n>` solo en un hijo;
 * - un hijo (`saleNumber` presente) tiene `commerceId` = `<saleNumber>-<n>`, con
 *   el mismo `n` de `jefNumber`.
 */
export const pickingSimulationOrderSchema = z
    .object({
    /** El pedido de Janis: el hijo, en una venta agrupada. */
    janisOrderId: requiredText(64),
    /** El hilo del DTE: en una venta agrupada, el de la venta, el mismo en todos sus hijos. */
    correlationId: requiredText(128),
    /** `NumAtCard` de la factura de reserva: el de la venta, en una agrupada. */
    reference: requiredText(100).nullable(),
    reserveInvoiceDocEntry: z.number().int().positive(),
    /** La bodega comun de las lineas del pedido; `null` si no hay una sola. */
    warehouse: z.string().nullable(),
    /** El numero de la venta en el canal si el pedido es hijo de una venta agrupada; si no, `null`. */
    saleNumber: requiredText(64).nullable(),
    /**
     * `<venta>-<n>`: el `commerceId` del hijo en Janis. `null` en un pedido que
     * no es hijo: el middleware no guarda su `commerceId` con el DTE.
     */
    commerceId: requiredText(64).nullable(),
    /** La fecha de picking de las lineas (`ShipDate`); `null` si no la llevan o no hay una sola. */
    shipDate: isoDateSchema.nullable(),
    /**
     * La orden de despacho del pedido (`<Documento>-<Numero_documento>`): la del
     * WMS si ya aviso; si no, la que armaria la simulacion (una por bodega y
     * fecha). `null` si las lineas no caben en una sola OD.
     */
    wmsDocument: requiredText(96).nullable(),
    /** El numero con que la parte del pedido se registra en JEF: `<tipo>-<folio>[-<n>]`. */
    jefNumber: z
        .string()
        .regex(JANIS_INVOICE_NUMBER_PATTERN, 'El numero en JEF es <tipo>-<folio> o <tipo>-<folio>-<n>.'),
    /** Hasta donde llego la parte del pedido (ver `PICKING_SIMULATION_SHARE_STATUSES`). */
    shareStatus: pickingSimulationShareStatusSchema,
    /** El DTE de la venta. */
    dte: z
        .object({
        documentType: dteDocumentTypeSchema,
        folio: z.number().int(),
        status: dteDocumentStatusSchema,
    })
        .strict(),
    lines: z.array(pickingSimulationLineSchema),
    /** `null` si el WMS todavia no mando ningun aviso. */
    picking: z
        .object({
        status: pickingStateStatusSchema,
        wmsStatus: z.enum(ALTANET_PICKING_ORDER_STATUSES),
        lastSequence: z.number().int(),
        lastEventAt: isoDateTimeSchema,
        janisStartPickingAt: isoDateTimeSchema.nullable(),
        janisPickedAt: isoDateTimeSchema.nullable(),
        readyCheckRequestedAt: isoDateTimeSchema.nullable(),
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((order, ctx) => {
    const base = `${String(order.dte.documentType)}-${String(order.dte.folio)}`;
    const parte = order.jefNumber.startsWith(`${base}-`)
        ? order.jefNumber.slice(base.length + 1)
        : null;
    if (order.jefNumber !== base && parte === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['jefNumber'],
            message: `El numero en JEF tiene que ser el del DTE (${base}), con o sin la parte.`,
        });
        return;
    }
    if (order.saleNumber === null) {
        if (parte !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['jefNumber'],
                message: 'Un pedido que no es hijo de una venta agrupada no lleva la parte en JEF.',
            });
        }
        return;
    }
    if (parte === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['jefNumber'],
            message: 'Un hijo de una venta agrupada registra su parte en JEF: <tipo>-<folio>-<n>.',
        });
        return;
    }
    if (order.commerceId !== `${order.saleNumber}-${parte}`) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['commerceId'],
            message: `El hijo ${parte} de la venta ${order.saleNumber} tiene commerceId ${order.saleNumber}-${parte}.`,
        });
    }
});
/** `GET /simulation/picking/orders` */
export const pickingSimulationOrderListSchema = z
    .object({
    /** `F9_PICKING_ENABLED`: si es `false`, el aviso se registra pero no llega a Janis. */
    pickingEnabled: z.boolean(),
    /**
     * `false` si `JANIS_DOMAIN` es produccion o desconocido: la simulacion no
     * escribe en Janis y el monitor deshabilita los botones.
     */
    janisWritesAllowed: z.boolean(),
    orders: z.array(pickingSimulationOrderSchema),
})
    .strict();
//# sourceMappingURL=picking.js.map